import { useMemo, useState, type ReactNode } from 'react';
import {
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CaretUpIcon,
  CaretUpDownIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/cn';
import { useScrollFade } from './useScrollFade';
import { formatNumber } from '@/lib/format';
import { Checkbox } from './Checkbox';
import { Skeleton } from './Skeleton';

export interface Column<T> {
  id: string;
  header: ReactNode;
  /** Cell renderer. Defaults to `String(row[id])`. */
  cell?: (row: T) => ReactNode;
  /** Value used for client-side sorting; enables sorting when provided (or `sortable`). */
  sortValue?: (row: T) => string | number | Date | null | undefined;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  /** CSS width, e.g. '30%' or '8rem'. */
  width?: string;
  /** Hide below the `md` breakpoint. */
  hideOnMobile?: boolean;
  className?: string;
}

export type SortState = { id: string; dir: 'asc' | 'desc' } | null;

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Rendered in place of the body when there are no rows (use <EmptyState size="inline" />). */
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Selection (checkbox column) — controlled. */
  selected?: ReadonlySet<string>;
  onSelectedChange?: (next: Set<string>) => void;
  /** Sorting: uncontrolled by default; pass `sort` + `onSortChange` for server-side sorting. */
  sort?: SortState;
  onSortChange?: (s: SortState) => void;
  defaultSort?: SortState;
  /** Client pagination page size (ignored when `pagination` is provided). */
  pageSize?: number;
  /** Server/cursor pagination: you render rows for the current page and handle navigation. */
  pagination?: {
    page: number;
    pageCount?: number;
    total?: number;
    hasNext: boolean;
    onPageChange: (p: number) => void;
  };
  /** Max height for the scroll area (enables sticky header scrolling). */
  maxHeight?: string;
  caption?: string;
  className?: string;
  labels?: {
    selectAll?: string;
    selectRow?: string;
    prev?: string;
    next?: string;
    of?: (from: number, to: number, total: number) => string;
  };
  /** Bulk-action bar content shown when rows are selected. */
  bulkActions?: (count: number) => ReactNode;
  density?: 'comfortable' | 'compact';
}

function compare(a: unknown, b: unknown) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'el', { sensitivity: 'base', numeric: true });
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  empty,
  onRowClick,
  selected,
  onSelectedChange,
  sort: sortProp,
  onSortChange,
  defaultSort = null,
  pageSize = 25,
  pagination,
  maxHeight,
  caption,
  className,
  labels = {},
  bulkActions,
  density = 'comfortable',
}: DataTableProps<T>) {
  const [innerSort, setInnerSort] = useState<SortState>(defaultSort);
  const fade = useScrollFade<HTMLDivElement>();
  const [page, setPage] = useState(0);
  const sort = sortProp !== undefined ? sortProp : innerSort;
  const serverSort = sortProp !== undefined;

  const sorted = useMemo(() => {
    if (!sort || serverSort) return rows;
    const col = columns.find((c) => c.id === sort.id);
    if (!col) return rows;
    const get = col.sortValue ?? ((r: T) => (r as Record<string, unknown>)[col.id] as string);
    const out = [...rows].sort((a, b) => compare(get(a), get(b)));
    return sort.dir === 'desc' ? out.reverse() : out;
  }, [rows, sort, columns, serverSort]);

  const clientPaged = !pagination;
  const pageCount = clientPaged
    ? Math.max(1, Math.ceil(sorted.length / pageSize))
    : (pagination.pageCount ?? 0);
  const currentPage = clientPaged ? Math.min(page, pageCount - 1) : pagination.page;
  const visible = clientPaged
    ? sorted.slice(currentPage * pageSize, currentPage * pageSize + pageSize)
    : sorted;

  const selectable = !!onSelectedChange;
  const sel = selected ?? new Set<string>();
  const visibleKeys = visible.map(rowKey);
  const allChecked = visibleKeys.length > 0 && visibleKeys.every((k) => sel.has(k));
  const someChecked = !allChecked && visibleKeys.some((k) => sel.has(k));

  const toggleSort = (c: Column<T>) => {
    if (!(c.sortable || c.sortValue)) return;
    const next: SortState =
      sort?.id !== c.id
        ? { id: c.id, dir: 'asc' }
        : sort.dir === 'asc'
          ? { id: c.id, dir: 'desc' }
          : null;
    if (onSortChange) onSortChange(next);
    if (!serverSort) setInnerSort(next);
    setPage(0);
  };

  const pad = density === 'compact' ? 'py-2' : 'py-3';
  const alignCls = (a?: string) =>
    a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';
  const total = clientPaged ? sorted.length : pagination.total;
  const from = clientPaged ? currentPage * pageSize + 1 : undefined;
  const to = clientPaged ? Math.min(sorted.length, (currentPage + 1) * pageSize) : undefined;

  return (
    <div className={cn('overflow-hidden rounded-lg border border-hairline bg-surface', className)}>
      {selectable && bulkActions && sel.size > 0 && (
        <div className="flex items-center justify-between gap-3 border-b border-hairline bg-primary-soft/60 px-4 py-2 text-sm animate-fade">
          {bulkActions(sel.size)}
        </div>
      )}
      {/* Edge fade while columns are hidden to the side (mobile): a cue to scroll. */}
      <div
        ref={fade}
        className="scroll-fade scroll-fade-table overflow-auto"
        style={maxHeight ? { maxHeight } : undefined}
      >
        <table className="w-full border-collapse text-[0.875rem]" aria-busy={loading || undefined}>
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead className="sticky top-0 z-[1] bg-sunken">
            <tr className="border-b border-hairline">
              {selectable && (
                <th scope="col" className="w-10 py-2.5 pl-4 pr-1">
                  <Checkbox
                    aria-label={labels.selectAll ?? 'Επιλογή όλων'}
                    checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                    onCheckedChange={(v) => {
                      const next = new Set(sel);
                      visibleKeys.forEach((k) => (v === true ? next.add(k) : next.delete(k)));
                      onSelectedChange(next);
                    }}
                  />
                </th>
              )}
              {columns.map((c) => {
                const canSort = c.sortable || !!c.sortValue;
                const active = sort?.id === c.id;
                const Icon = active
                  ? sort!.dir === 'asc'
                    ? CaretUpIcon
                    : CaretDownIcon
                  : CaretUpDownIcon;
                return (
                  <th
                    key={c.id}
                    scope="col"
                    style={c.width ? { width: c.width } : undefined}
                    aria-sort={
                      active
                        ? sort!.dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : canSort
                          ? 'none'
                          : undefined
                    }
                    className={cn(
                      'px-4 py-2.5 text-2xs font-bold uppercase tracking-eyebrow text-ink-muted',
                      alignCls(c.align),
                      c.hideOnMobile && 'max-md:hidden',
                      c.className,
                    )}
                  >
                    {canSort ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(c)}
                        className={cn(
                          'group inline-flex items-center gap-1 uppercase hover:text-ink focus-visible:outline-2 focus-visible:outline-focus',
                          active && 'text-ink',
                          c.align === 'right' && 'flex-row-reverse',
                        )}
                      >
                        {c.header}
                        <Icon
                          weight="bold"
                          className={cn(
                            'size-3 transition-opacity',
                            active ? 'opacity-100' : 'opacity-0 group-hover:opacity-60',
                          )}
                        />
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }, (_, i) => (
                <tr key={i} className="border-b border-hairline last:border-0">
                  {selectable && (
                    <td className="py-3 pl-4">
                      <Skeleton className="size-4" />
                    </td>
                  )}
                  {columns.map((c, j) => (
                    <td key={c.id} className={cn('px-4', pad, c.hideOnMobile && 'max-md:hidden')}>
                      <Skeleton
                        className={cn(
                          'h-3.5',
                          j === 0 ? 'w-40' : 'w-20',
                          c.align === 'right' && 'ml-auto',
                        )}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)}>
                  {empty ?? <div className="py-12 text-center text-ink-muted">–</div>}
                </td>
              </tr>
            ) : (
              visible.map((row) => {
                const k = rowKey(row);
                const isSel = sel.has(k);
                return (
                  <tr
                    key={k}
                    data-selected={isSel || undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      'border-b border-hairline transition-colors last:border-0',
                      onRowClick && 'cursor-pointer hover:bg-canvas',
                      isSel && 'bg-primary-soft/40 hover:bg-primary-soft/60',
                    )}
                  >
                    {selectable && (
                      <td className="w-10 py-3 pl-4 pr-1" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          aria-label={labels.selectRow ?? 'Επιλογή γραμμής'}
                          checked={isSel}
                          onCheckedChange={(v) => {
                            const next = new Set(sel);
                            if (v === true) next.add(k);
                            else next.delete(k);
                            onSelectedChange(next);
                          }}
                        />
                      </td>
                    )}
                    {columns.map((c) => (
                      <td
                        key={c.id}
                        className={cn(
                          'px-4 text-ink-soft',
                          pad,
                          alignCls(c.align),
                          c.align === 'right' && 'num',
                          c.hideOnMobile && 'max-md:hidden',
                          c.className,
                        )}
                      >
                        {c.cell
                          ? c.cell(row)
                          : String((row as Record<string, unknown>)[c.id] ?? '')}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {!loading &&
        (pageCount > 1 || (pagination && (pagination.hasNext || pagination.page > 0))) && (
          <div className="flex items-center justify-between gap-4 border-t border-hairline px-4 py-2.5 text-sm text-ink-muted">
            <span className="num">
              {from !== undefined && to !== undefined && total !== undefined
                ? (labels.of?.(from, to, total) ??
                  `${formatNumber(from)}–${formatNumber(to)} από ${formatNumber(total)}`)
                : total !== undefined
                  ? formatNumber(total)
                  : ''}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="inline-flex size-8 items-center justify-center rounded-sm hover:bg-sunken disabled:opacity-40"
                aria-label={labels.prev ?? 'Προηγούμενη σελίδα'}
                disabled={currentPage <= 0}
                onClick={() =>
                  clientPaged ? setPage(currentPage - 1) : pagination.onPageChange(currentPage - 1)
                }
              >
                <CaretLeftIcon className="size-4" />
              </button>
              <span className="num min-w-12 text-center text-ink">
                {currentPage + 1}
                {pageCount ? <span className="text-ink-subtle"> / {pageCount}</span> : null}
              </span>
              <button
                type="button"
                className="inline-flex size-8 items-center justify-center rounded-sm hover:bg-sunken disabled:opacity-40"
                aria-label={labels.next ?? 'Επόμενη σελίδα'}
                disabled={clientPaged ? currentPage >= pageCount - 1 : !pagination.hasNext}
                onClick={() =>
                  clientPaged ? setPage(currentPage + 1) : pagination.onPageChange(currentPage + 1)
                }
              >
                <CaretRightIcon className="size-4" />
              </button>
            </div>
          </div>
        )}
    </div>
  );
}
